import { api } from '../../core/network/api';

/** GET /api/account/me — everything the Settings page shows about the account. */
export interface AccountInfo {
  email: string;
  username: string | null;
  role: 'PLAYER' | 'ADMIN' | 'KYC_ADMIN' | string;
  createdAt: string;
  emailVerified: boolean;
  phone: string | null;
  /** Billing country (ISO-2) — drives invoice VAT. */
  country: string | null;
  kycStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED' | string;
  kycRejectReason: string | null;
  kycSubmittedAt: string | null;
  /** `feature.kyc` game setting — hides the Papers tab content when off. */
  kycEnabled: boolean;
  /** Billing identity — printed on invoice PDFs under "Bill To". */
  firstName: string | null;
  lastName: string | null;
  address: string | null;
}

export const fetchAccount = () => api.get<AccountInfo>('/account/me');

/** POST /api/account/change-password — verifies the current password first. */
export const changePassword = (currentPassword: string, newPassword: string) =>
  api.post<{ ok: boolean }>('/account/change-password', { currentPassword, newPassword });

/**
 * PATCH /api/account/profile — billing identity (name, surname, address, phone,
 * country). Only the keys sent are written, so a partial patch is safe.
 * Returns the refreshed account.
 */
export const updateProfile = (patch: {
  firstName?: string;
  lastName?: string;
  address?: string;
  phone?: string;
  country?: string;
}) => api.patch<AccountInfo>('/account/profile', patch);

/**
 * POST /api/account/delete — close the account for good.
 * Password-confirmed; the server ends every session, so the caller must log out.
 */
export const deleteAccount = (password: string) =>
  api.post<{ ok: boolean }>('/account/delete', { password });

/** POST /api/account/contact-support — email the house support address. */
export const contactSupport = (subject: string, message: string) =>
  api.post<{ ok: boolean }>('/account/contact-support', { subject, message });

/** POST /api/account/resend-verification — re-send the verify-email mail. */
export const resendVerification = () =>
  api.post<{ ok: boolean }>('/account/resend-verification', {});

/** PATCH /api/account/country — billing country (dominion's /user/country). */
export const setCountry = (country: string) =>
  api.patch<{ ok: boolean; country: string }>('/account/country', { country });

/** POST /api/kyc/submit — identity papers (core endpoint, multipart). */
export function submitKyc(input: {
  fullName: string;
  dateOfBirth: string; // yyyy-mm-dd
  address: string;
  country: string; // ISO-2
  document: File;
}): Promise<{ status: string }> {
  const form = new FormData();
  form.set('fullName', input.fullName);
  form.set('dateOfBirth', input.dateOfBirth);
  form.set('address', input.address);
  form.set('country', input.country);
  form.set('document', input.document);
  // Override the client's JSON default so axios sets the multipart boundary.
  return api.post<{ status: string }>('/kyc/submit', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}
